import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Input } from '@progress/kendo-react-inputs';
import Button from '@components-clean/core/button';
import CardAction from '@components-clean/core/card/card-actions';
import Dialog from '@components-clean/core/dialog';
import Card from 'views-clean/components/core/card';
import EditCard from 'views-clean/components/core/edit-card';
import { useBoolean } from '@hooks/index';
import NotificationService from '@utils/notification';

interface CardsPageProps {
    isDisabled: boolean;
}

const CardsPage = ({ isDisabled }: CardsPageProps) => {
    const [initialState, setInitialState] = useState<{
        firstName: string;
        lastName: string;
    }>({
        firstName: 'James',
        lastName: 'Bond',
    });
    const [editState, setEditState] = useState<{
        firstName: string;
        lastName: string;
    }>(initialState);
    const isEditMode = useBoolean(false);
    const showDelete = useBoolean(false);

    const [addActionClicks, setAddActionClicks] = useState(0);
    const [removeActionClicks, setRemoveActionClicks] = useState(0);
    const [editActionClicks, setEditActionClicks] = useState(0);
    const controlledCardCollapsed = useBoolean(false);

    return (
        <div>
            {showDelete.value && (
                <Dialog
                    data-testid="delete-invoked-dialog"
                    onClose={() => showDelete.setFalse()}
                    closeIcon
                    title="Delete Invoked"
                />
            )}
            <Card
                title={
                    <div>
                        <FontAwesomeIcon icon={['far', 'star']} className="fa-sm mr-5" />
                        Title
                    </div>
                }
                data-testid="core-card"
            >
                <p>
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Praesent condimentum libero tellus, sit
                    amet mattis est aliquet at. Maecenas ultricies facilisis maximus. Sed varius nisl eu est mollis, id
                    ultricies lectus pharetra. Quisque vel odio dignissim, lacinia lorem vitae, pulvinar diam. Duis non
                    tortor metus. Fusce pellentesque lectus ac libero sodales fermentum. Phasellus interdum nulla enim,
                    sed ultrices elit ullamcorper in. Vestibulum id erat ut ante gravida hendrerit a et sapien.
                </p>
            </Card>
            <br />
            <EditCard
                title="Editable card"
                onEdit={() => isEditMode.setTrue()}
                onCancel={() => {
                    isEditMode.setFalse();
                    setEditState({ ...initialState });
                }}
                onSave={() => {
                    isEditMode.setFalse();
                    setInitialState({ ...editState });
                    NotificationService.showNotification('Card has been saved');
                }}
                isEditMode={isEditMode.value}
                disabled={isDisabled}
                data-testid="edit-core-card"
                onDelete={() => showDelete.setTrue()}
            >
                {/* Test block */}
                <>
                    <i>
                        <strong>
                            Form below is just a demo form using standard inputs, instead of our Opra-themed textboxes.
                            Just to demonstrated edit functionality.
                        </strong>
                    </i>
                    <div style={{ display: 'flex', gap: '30px', padding: '20px 0' }}>
                        <Input
                            type="text"
                            label="First Name"
                            placeholder="First Name"
                            value={editState.firstName}
                            onChange={(e) =>
                                setEditState({
                                    ...editState,
                                    firstName: e.value,
                                })
                            }
                            disabled={!isEditMode.value || isDisabled}
                        />
                        <Input
                            type="text"
                            label="Last Name"
                            placeholder="Last Name"
                            value={editState.lastName}
                            onChange={(e) =>
                                setEditState({
                                    ...editState,
                                    lastName: e.value,
                                })
                            }
                            disabled={!isEditMode.value || isDisabled}
                        />
                    </div>
                </>
            </EditCard>

            <br />

            <Card data-testid="collapsible-card" title="Collapsible Card" collapsible>
                <p>Collapsible functionality is enabled by using the `collapsible` prop.</p>

                <p>By default, the collapsed state is handled internally.</p>
            </Card>

            <br />

            <Card
                data-testid="collapsible-card-controlled"
                title={`Controlled Collapsible Card - State: ${controlledCardCollapsed.value.toString()}`}
                collapsible
                collapsed={controlledCardCollapsed.value}
                onCollapsedChange={controlledCardCollapsed.setValue}
            >
                <p>
                    Collapsible functionality can be controlled by the parent component using the `collapsed` and
                    `onCollapsedChange` props.
                </p>
            </Card>

            <br />

            <Card
                data-testid="collapsible-card-with-actions"
                title="Collapsible Card with Other Actions"
                collapsible
                headerActions={
                    <div className="k-d-flex k-align-items-baseline k-gap-2.5 k-mr-2.5">
                        <Button data-testid="action-1" faIcon={['far', 'undo']}>
                            Undo
                        </Button>
                        <Button data-testid="action-1" faIcon={['far', 'xmark']}>
                            Cancel
                        </Button>
                        <Button data-testid="action-1" faIcon={['far', 'save']}>
                            Save
                        </Button>
                    </div>
                }
            >
                <p>
                    The collapse button will be placed at the end of the actions section of the card header. (after the
                    `headerActions`)
                </p>

                <p>
                    The `headerActions` will need to be styled so that the collapse button fits in with them, such as
                    having appropriate margin-right, e.g.
                </p>

                <pre>{`<Card
    ...
    collapsible
    headerActions={
        // The \`headerActions\` of this card
        <div className="k-d-flex k-align-items-baseline k-gap-2.5 k-mr-2.5">
            <Button data-testid="action-1" faIcon={['far', 'undo']}>
                Undo
            </Button>
            <Button data-testid="action-1" faIcon={['far', 'xmark']}>
                Cancel
            </Button>
            <Button data-testid="action-1" faIcon={['far', 'save']}>
                Save
            </Button>
        </div>
    }
/>
   ...
</Card>`}</pre>
            </Card>

            <br />

            <Card
                title="Card with Action Buttons"
                data-testid="card-with-actions"
                headerActions={
                    <div className="k-d-flex k-align-items-baseline k-gap-2.5">
                        <CardAction
                            data-testid="card-add-action"
                            faIcon={['far', 'plus']}
                            onClick={() => setAddActionClicks((x) => x + 1)}
                        />
                        <CardAction
                            data-testid="card-add-action"
                            faIcon={['far', 'minus']}
                            onClick={() => setRemoveActionClicks((x) => x + 1)}
                        />
                        <CardAction
                            data-testid="card-add-action"
                            faIcon={['far', 'edit']}
                            onClick={() => setEditActionClicks((x) => x + 1)}
                        />
                    </div>
                }
            >
                <div>Add action clicks: {addActionClicks}</div>
                <div>Remove action clicks: {removeActionClicks}</div>
                <div>Edit action clicks: {editActionClicks}</div>
            </Card>
        </div>
    );
};

export default CardsPage;
